/**
 * StudioBestTimeToPost — Surfaces the analytics-derived best posting
 * time so the user can pick that slot when scheduling.
 *
 * Extracted from the old ``StudioPlatformPreview`` and relocated into
 * the Publish Panel, which is the actual decision point for scheduling.
 *
 * Clicking the time pill auto-fills the Schedule date + time inputs
 * (via the provided ``onPick`` callback). If the parent doesn't pass
 * ``onPick`` the pill is still informative but not interactive.
 */

import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { analyticsAPI } from '@/services/api';

interface Props {
  /** Called with the recommended (YYYY-MM-DD, HH:mm) pair. */
  onPick?: (date: string, time: string) => void;
  /** Whether to render the pill only when the user is actively choosing
   * a scheduled time (hides the suggestion in 'now' / 'queue' modes). */
  visible?: boolean;
}

/** Next occurrence of a given weekday name in YYYY-MM-DD. */
function nextWeekdayISODate(day: string): string | null {
  const DAYS: Record<string, number> = {
    sunday: 0,
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
  };
  const target = DAYS[day.toLowerCase()];
  if (target === undefined) {
    return null;
  }
  const now = new Date();
  const delta = (target - now.getDay() + 7) % 7 || 7; // always future
  const next = new Date(now);
  next.setDate(now.getDate() + delta);
  return next.toISOString().split('T')[0] ?? null;
}

/** Normalize the backend's time format ("8:00 PM", "20:00") to "HH:mm". */
function normalizeTime(raw: string): string | null {
  const trimmed = raw.trim();
  const twelve = trimmed.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (twelve) {
    let hours = parseInt(twelve[1] ?? '0', 10);
    const minutes = twelve[2] ?? '00';
    const suffix = (twelve[3] ?? '').toUpperCase();
    if (suffix === 'PM' && hours < 12) {
      hours += 12;
    } else if (suffix === 'AM' && hours === 12) {
      hours = 0;
    }
    return `${hours.toString().padStart(2, '0')}:${minutes}`;
  }
  const twenty = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (twenty) {
    return `${(twenty[1] ?? '0').padStart(2, '0')}:${twenty[2]}`;
  }
  return null;
}

export default function StudioBestTimeToPost({ onPick, visible = true }: Props) {
  const { t } = useTranslation();

  const { data: bestTimesData } = useQuery({
    queryKey: ['analytics-best-times'],
    queryFn: () => analyticsAPI.bestTimes(),
    staleTime: 5 * 60 * 1000,
  });

  const top = useMemo(() => {
    const recs = bestTimesData?.data?.recommendations;
    if (!Array.isArray(recs) || recs.length === 0) {
      return null;
    }
    return recs[0] as { day?: string; time?: string };
  }, [bestTimesData]);

  if (!visible || !top || (!top.day && !top.time)) {
    return null;
  }

  const label = top.day && top.time
    ? `${top.day} at ${top.time}`
    : top.time || '';

  const canPick = Boolean(onPick && top.day && top.time);

  const handlePick = () => {
    if (!canPick || !onPick) {
      return;
    }
    const date = nextWeekdayISODate(top.day as string);
    const time = normalizeTime(top.time as string);
    if (date && time) {
      onPick(date, time);
    }
  };

  const content = (
    <>
      <Clock size={12} className="text-amber-500 flex-shrink-0" aria-hidden="true" />
      <span className="text-[11px] text-amber-700">
        {t('studio_best_time_label', 'Best time to post:')}{' '}
        <strong>{label}</strong>
      </span>
    </>
  );

  if (canPick) {
    return (
      <button
        type="button"
        onClick={handlePick}
        className="w-full flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 hover:bg-amber-100 focus:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-400 rounded-lg transition-colors text-left"
        title={t(
          'studio_best_time_click_to_apply',
          'Click to apply this time to the schedule',
        )}
      >
        {content}
        <span className="ml-auto text-[10px] font-semibold text-amber-700 uppercase tracking-wide">
          {t('studio_best_time_use', 'Use')}
        </span>
      </button>
    );
  }

  return (
    <div className="w-full flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 rounded-lg">
      {content}
    </div>
  );
}
